import {
  clearGetAllMembers,
  clearGetMemberById,
  deleteMember,
  getAllMembers,
  getMemberById,
  updateMember,
  cancelMembership,
  extendMembership,
  retryPaymentMembership,
  deleteMemberProfile,
  retrySubscriptionPayment,
  getAllNonMembers,
  clearGetAllNonMembers,
  updateNonMember,
  getAllFreeMembers,
  getBuildingDataById,
  clearGetBuildingDataById,
  addFreeMember,
  getParkingDataByBuildingId,
  clearGetParkingDataByBuildingId,
  checkMemberExist,
  syncMembersData,
} from "./MembersActions";
import { createSlice } from "@reduxjs/toolkit";

interface MembersState {
  members: any[] | null;
  membersLoading: boolean;
  membersError: any | null;

  nonMembers: any[] | null;
  nonMembersLoading: boolean;
  nonMembersError: any | null;

  updateMember: any | null;
  updateMemberLoading: boolean;
  updateMemberError: any | null;

  updateNonMember: any | null;
  updateNonMemberLoading: boolean;
  updateNonMemberError: any | null;

  deleteMember: any | null;
  deleteMemberLoading: boolean;
  deleteMemberError: any | null;

  memberById: any | null;
  memberByIdLoading: boolean;
  memberByIdError: any | null;

  cancelMembership: any | null;
  cancelMembershipLoading: boolean;
  cancelMembershipError: any | null;

  extendMembership: any | null;
  extendMembershipLoading: boolean;
  extendMembershipError: any | null;

  syncMembersData: any | null;
  syncMembersDataLoading: boolean;
  syncMembersDataError: any | null;

  retryPaymentMembership: any | null;
  retryPaymentMembershipLoading: boolean;
  retryPaymentMembershipError: any | null;

  deleteMemberProfile: any | null;
  deleteMemberProfileLoading: boolean;
  deleteMemberProfileError: any | null;

  retrySubscriptionPaymentData: any | null;
  retrySubscriptionPaymentLoading: boolean;
  retrySubscriptionPaymentError: any | null;

  freeMembers: any[] | null;
  freeMembersLoading: boolean;
  freeMembersError: any | null;

  buildingDataById: any | null;
  buildingDataByIdLoading: boolean;
  buildingDataByIdError: any | null;

  addFreeMember: any | null;
  addFreeMemberLoading: boolean;
  addFreeMemberError: any | null;

  parkingDataByBuildingId: any | null;
  parkingDataByBuildingIdLoading: boolean;
  parkingDataByBuildingIdError: any | null;

  checkMemberExist: any | null;
  checkMemberExistLoading: boolean;
  checkMemberExistError: any | null;
}

const initialState: MembersState = {
  members: null,
  membersLoading: false,
  membersError: null,

  nonMembers: null,
  nonMembersLoading: false,
  nonMembersError: null,

  updateMember: null,
  updateMemberLoading: false,
  updateMemberError: null,

  updateNonMember: null,
  updateNonMemberLoading: false,
  updateNonMemberError: null,

  deleteMember: null,
  deleteMemberLoading: false,
  deleteMemberError: null,

  memberById: null,
  memberByIdLoading: false,
  memberByIdError: null,

  cancelMembership: null,
  cancelMembershipLoading: false,
  cancelMembershipError: null,

  extendMembership: null,
  extendMembershipLoading: false,
  extendMembershipError: null,

  syncMembersData: null,
  syncMembersDataLoading: false,
  syncMembersDataError: null,

  retryPaymentMembership: null,
  retryPaymentMembershipLoading: false,
  retryPaymentMembershipError: null,

  deleteMemberProfile: null,
  deleteMemberProfileLoading: false,
  deleteMemberProfileError: null,

  retrySubscriptionPaymentData: null,
  retrySubscriptionPaymentLoading: false,
  retrySubscriptionPaymentError: null,

  freeMembers: null,
  freeMembersLoading: false,
  freeMembersError: null,

  buildingDataById: null,
  buildingDataByIdLoading: false,
  buildingDataByIdError: null,

  addFreeMember: null,
  addFreeMemberLoading: false,
  addFreeMemberError: null,

  parkingDataByBuildingId: null,
  parkingDataByBuildingIdLoading: false,
  parkingDataByBuildingIdError: null,

  checkMemberExist: null,
  checkMemberExistLoading: false,
  checkMemberExistError: null,
};

const membersSlice = createSlice({
  name: "members",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllMembers.pending, (state) => {
      state.membersLoading = true;
      state.membersError = null;
      state.members = null;
    });
    builder.addCase(getAllMembers.fulfilled, (state, action) => {
      state.membersLoading = false;
      state.members = action.payload;
      state.membersError = null;
    });
    builder.addCase(getAllMembers.rejected, (state, action) => {
      state.membersLoading = false;
      state.membersError = action.payload;
      state.members = null;
    });
    builder.addCase(getAllFreeMembers.pending, (state) => {
      state.freeMembersLoading = true;
      state.freeMembersError = null;
      state.freeMembers = null;
    });
    builder.addCase(getAllFreeMembers.fulfilled, (state, action) => {
      state.freeMembersLoading = false;
      state.freeMembers = action.payload;
      state.freeMembersError = null;
    });
    builder.addCase(getAllFreeMembers.rejected, (state, action) => {
      state.freeMembersLoading = false;
      state.freeMembersError = action.payload;
      state.freeMembers = null;
    });
    builder.addCase(getBuildingDataById.pending, (state) => {
      state.buildingDataByIdLoading = true;
      state.buildingDataByIdError = null;
      state.buildingDataById = null;
    });
    builder.addCase(getBuildingDataById.fulfilled, (state, action) => {
      state.buildingDataByIdLoading = false;
      state.buildingDataById = action.payload;
      state.buildingDataByIdError = null;
    });
    builder.addCase(getBuildingDataById.rejected, (state, action) => {
      state.buildingDataByIdLoading = false;
      state.buildingDataByIdError = action.payload;
      state.buildingDataById = null;
    });
    builder.addCase(clearGetBuildingDataById.fulfilled, (state) => {
      state.buildingDataByIdLoading = false;
      state.buildingDataByIdError = null;
      state.buildingDataById = null;
    });
    builder.addCase(getAllNonMembers.pending, (state) => {
      state.nonMembersLoading = true;
      state.nonMembersError = null;
      state.nonMembers = null;
    });
    builder.addCase(getAllNonMembers.fulfilled, (state, action) => {
      state.nonMembersLoading = false;
      state.nonMembers = action.payload;
      state.nonMembersError = null;
    });
    builder.addCase(getAllNonMembers.rejected, (state, action) => {
      state.nonMembersLoading = false;
      state.nonMembersError = action.payload;
      state.nonMembers = null;
    });
    builder.addCase(updateMember.pending, (state) => {
      state.updateMemberLoading = true;
      state.updateMemberError = null;
    });
    builder.addCase(updateMember.fulfilled, (state, action) => {
      state.updateMemberLoading = false;
      state.updateMemberError = null;
      state.updateMember = action.payload;
    });
    builder.addCase(updateMember.rejected, (state, action) => {
      state.updateMemberLoading = false;
      state.updateMemberError = action.payload;
      state.updateMember = null;
    });
    builder.addCase(updateNonMember.pending, (state) => {
      state.updateNonMemberLoading = true;
      state.updateNonMemberError = null;
    });
    builder.addCase(updateNonMember.fulfilled, (state, action) => {
      state.updateNonMemberLoading = false;
      state.updateNonMemberError = null;
      state.updateNonMember = action.payload;
    });
    builder.addCase(updateNonMember.rejected, (state, action) => {
      state.updateNonMemberLoading = false;
      state.updateNonMemberError = action.payload;
      state.updateNonMember = null;
    });
    builder.addCase(deleteMember.pending, (state) => {
      state.deleteMemberLoading = true;
      state.deleteMemberError = null;
    });
    builder.addCase(deleteMember.fulfilled, (state, action) => {
      state.deleteMemberLoading = false;
      state.deleteMemberError = null;
      state.deleteMember = action.payload;
    });
    builder.addCase(deleteMember.rejected, (state, action) => {
      state.deleteMemberLoading = false;
      state.deleteMemberError = action.payload;
      state.deleteMember = null;
    });
    builder.addCase(getMemberById.pending, (state) => {
      state.memberByIdLoading = true;
      state.memberByIdError = null;
    });
    builder.addCase(getMemberById.fulfilled, (state, action) => {
      state.memberByIdLoading = false;
      state.memberByIdError = null;
      state.memberById = action.payload;
    });
    builder.addCase(getMemberById.rejected, (state, action) => {
      state.memberByIdLoading = false;
      state.memberByIdError = action.payload;
      state.memberById = null;
    });

    builder.addCase(clearGetMemberById.fulfilled, (state) => {
      state.memberByIdLoading = false;
      state.memberByIdError = null;
      state.memberById = null;
    });

    builder.addCase(clearGetAllMembers.fulfilled, (state) => {
      state.membersLoading = false;
      state.membersError = null;
      state.members = null;
    });
    builder.addCase(clearGetAllNonMembers.fulfilled, (state) => {
      state.nonMembersLoading = false;
      state.nonMembersError = null;
      state.nonMembers = null;
    });
    builder.addCase(cancelMembership.pending, (state) => {
      state.cancelMembershipLoading = true;
      state.cancelMembershipError = null;
      state.cancelMembership = null;
    });
    builder.addCase(cancelMembership.fulfilled, (state, action) => {
      state.cancelMembershipLoading = false;
      state.cancelMembershipError = null;
      state.cancelMembership = action.payload;
    });
    builder.addCase(cancelMembership.rejected, (state, action) => {
      state.cancelMembershipLoading = false;
      state.cancelMembershipError = action.payload;
      state.cancelMembership = null;
    });
    builder.addCase(extendMembership.pending, (state) => {
      state.extendMembershipLoading = true;
      state.extendMembershipError = null;
      state.extendMembership = null;
    });
    builder.addCase(extendMembership.fulfilled, (state, action) => {
      state.extendMembershipLoading = false;
      state.extendMembershipError = null;
      state.extendMembership = action.payload;
    });
    builder.addCase(extendMembership.rejected, (state, action) => {
      state.extendMembershipLoading = false;
      state.extendMembershipError = action.payload;
      state.extendMembership = null;
    });
    builder.addCase(syncMembersData.pending, (state) => {
      state.syncMembersDataLoading = true;
      state.syncMembersDataError = null;
      state.syncMembersData = null;
    });
    builder.addCase(syncMembersData.fulfilled, (state, action) => {
      state.syncMembersDataLoading = false;
      state.syncMembersDataError = null;
      state.syncMembersData = action.payload;
    });
    builder.addCase(syncMembersData.rejected, (state, action) => {
      state.syncMembersDataLoading = false;
      state.syncMembersDataError = action.payload;
      state.syncMembersData = null;
    });
    builder.addCase(retryPaymentMembership.pending, (state) => {
      state.retryPaymentMembershipLoading = true;
      state.retryPaymentMembershipError = null;
      state.retryPaymentMembership = null;
    });
    builder.addCase(retryPaymentMembership.fulfilled, (state, action) => {
      state.retryPaymentMembershipLoading = false;
      state.retryPaymentMembershipError = null;
      state.retryPaymentMembership = action.payload;
    });
    builder.addCase(retryPaymentMembership.rejected, (state, action) => {
      state.retryPaymentMembershipLoading = false;
      state.retryPaymentMembershipError = action.payload;
      state.retryPaymentMembership = null;
    });
    builder.addCase(deleteMemberProfile.pending, (state) => {
      state.deleteMemberProfileLoading = true;
      state.deleteMemberProfileError = null;
      state.deleteMemberProfile = null;
    });
    builder.addCase(deleteMemberProfile.fulfilled, (state, action) => {
      state.deleteMemberProfileLoading = false;
      state.deleteMemberProfileError = null;
      state.deleteMemberProfile = action.payload;
    });
    builder.addCase(deleteMemberProfile.rejected, (state, action) => {
      state.deleteMemberProfileLoading = false;
      state.deleteMemberProfileError = action.payload;
      state.deleteMemberProfile = null;
    });

    builder.addCase(retrySubscriptionPayment.pending, (state) => {
      state.retrySubscriptionPaymentLoading = true;
      state.retrySubscriptionPaymentError = null;
      state.retrySubscriptionPaymentData = null;
    });
    builder.addCase(retrySubscriptionPayment.fulfilled, (state, action) => {
      state.retrySubscriptionPaymentLoading = false;
      state.retrySubscriptionPaymentError = null;
      state.retrySubscriptionPaymentData = action.payload;
    });
    builder.addCase(retrySubscriptionPayment.rejected, (state, action) => {
      state.retrySubscriptionPaymentLoading = false;
      state.retrySubscriptionPaymentError = action.payload;
      state.retrySubscriptionPaymentData = null;
    });
    builder.addCase(addFreeMember.pending, (state) => {
      state.addFreeMemberLoading = true;
      state.addFreeMemberError = null;
      state.addFreeMember = null;
    });
    builder.addCase(addFreeMember.fulfilled, (state, action) => {
      state.addFreeMemberLoading = false;
      state.addFreeMemberError = null;
      state.addFreeMember = action.payload;
    });
    builder.addCase(addFreeMember.rejected, (state, action) => {
      state.addFreeMemberLoading = false;
      state.addFreeMemberError = action.payload;
      state.addFreeMember = null;
    });
    builder.addCase(getParkingDataByBuildingId.pending, (state) => {
      state.parkingDataByBuildingIdLoading = true;
      state.parkingDataByBuildingIdError = null;
      state.parkingDataByBuildingId = null;
    });
    builder.addCase(getParkingDataByBuildingId.fulfilled, (state, action) => {
      state.parkingDataByBuildingIdLoading = false;
      state.parkingDataByBuildingIdError = null;
      state.parkingDataByBuildingId = action.payload;
    });
    builder.addCase(getParkingDataByBuildingId.rejected, (state, action) => {
      state.parkingDataByBuildingIdLoading = false;
      state.parkingDataByBuildingIdError = action.payload;
      state.parkingDataByBuildingId = null;
    });
    builder.addCase(clearGetParkingDataByBuildingId.fulfilled, (state) => {
      state.parkingDataByBuildingIdLoading = false;
      state.parkingDataByBuildingIdError = null;
      state.parkingDataByBuildingId = null;
    });
    builder.addCase(checkMemberExist.pending, (state) => {
      state.checkMemberExistLoading = true;
      state.checkMemberExistError = null;
      state.checkMemberExist = null;
    });
    builder.addCase(checkMemberExist.fulfilled, (state, action) => {
      state.checkMemberExistLoading = false;
      state.checkMemberExistError = null;
      state.checkMemberExist = action.payload;
    });
    builder.addCase(checkMemberExist.rejected, (state, action) => {
      state.checkMemberExistLoading = false;
      state.checkMemberExistError = action.payload;
      state.checkMemberExist = null;
    });
  },
});

const membersReducer = membersSlice.reducer;

export default membersReducer;
